import * as THREE from 'three';
import { shadowBlastEffect as LOOK, shadowCantripEffect as SHADE } from '../../../content/spells/effects.js';
import { DarkSmoke, softGlowTexture } from './dark-smoke.js';
import type { CantripCast } from './cantrip-charges.js';

/** The Shadowmancer's cantrips, drawn by `ShadowCharge` rather than the other cantrips' motes of light. */
export type ShadowCantrip = 'shadow-blast' | 'summon-shadow' | 'command-shadows';
export const isShadowCantrip = (kind: string): kind is ShadowCantrip => kind === 'shadow-blast' || kind === 'summon-shadow' || kind === 'command-shadows';

/**
 * Where the clip lets go (`CANTRIP_RELEASE`) and how long what is left after it takes to fade, in clip progress (the clip is 2.5 s, so .2 is half a
 * second); Summon Shadow's pool and its run to the Shadow take longest, seeping away as the Shadow rises. It runs on past the clip's end.
 */
const RELEASE = .8, AFTER = .2, POOL_AFTER = .5;
const KNOT = 9, WISPS = 12, POOL = 16;
/** Of Summon Shadow's pool puffs, the ones under the hand; the rest run along the ground to where the Shadow rises. */
const UNDER = 7;
const UP = new THREE.Vector3(0, 1, 0);
const smooth = (x: number, a: number, b: number) => THREE.MathUtils.smoothstep(x, a, b);
/** A stable number from 0 to 1 for a puff and a caster. */
const hash = (i: number, seed: number) => { const x = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453; return x - Math.floor(x); };

/**
 * The dark a Shadowmancer gathers in the clawed off hand, in the same black smoke as Shadow Blast (`dark-smoke.ts`): for Shadow Blast a knot
 * of it grows in the palm, fed by wisps drawn up into it, and is gone the moment it is hurled (the flight takes it from there); for Summon
 * Shadow it drips from the palm pressed toward the ground, spreads there into a pool and runs across the ground to where the Shadow will rise; for Command Shadows it winds round
 * the claws and is flung at the foe with the sweep. A faint cold light keeps it readable on dark ground. Everything is a function of the
 * clip's progress and the game clock, so a paused game freezes it.
 */
export class ShadowCharge {
  readonly group = new THREE.Group();
  private smoke: DarkSmoke;
  private halo: THREE.Sprite;
  constructor(readonly kind: ShadowCantrip, private seed: number, private groundHeight: (x: number, z: number) => number = () => 0) {
    this.group.name = `${kind}-charge`;
    this.smoke = new DarkSmoke(KNOT + WISPS + POOL, LOOK.color, LOOK.rim, `${kind}-charge-smoke`);
    this.halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: softGlowTexture(), color: LOOK.glow, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false, opacity: 0 }));
    this.halo.renderOrder = 2;
    this.group.add(this.halo, this.smoke.mesh);
  }
  /** Poses the charge for clip progress `cast.progress` at game time `time`; returns false once it has nothing left to show. */
  update(cast: CantripCast, time: number, _ppm: number) {
    const p = cast.progress, hand = cast.hand, forward = cast.facing, side = new THREE.Vector3().crossVectors(UP, forward).normalize();
    const after = this.kind === 'summon-shadow' ? POOL_AFTER : AFTER;
    const charge = smooth(p, .08, .74), released = p >= RELEASE, q = THREE.MathUtils.clamp((p - RELEASE) / after, 0, 1);
    const { smoke } = this, point = new THREE.Vector3();
    let light = 0;
    for (let i = 0; i < KNOT; i++) {
      // The knot in the palm, only for Shadow Blast: lumps tumbling about its heart, growing with the charge.
      if (this.kind !== 'shadow-blast' || released) { smoke.hide(i); continue; }
      const a = time * (4 + i * .7) + i * 2.4, r = SHADE.knot * (.2 + .4 * hash(i, this.seed)) * charge;
      smoke.set(i, hand.x + Math.cos(a) * r, hand.y + .04 * charge + Math.sin(a * 1.3) * r * .8, hand.z + Math.sin(a) * r,
        SHADE.knot * (.5 + 1.6 * charge) * (1.2 - i * .06), .95 * smooth(p, .05, .2), i < 2 ? .25 : .8, time * (.3 + i * .1), i * .29 + this.seed);
    }
    // The knot's cold light outlasts it a moment, dimming as it flies.
    if (this.kind === 'shadow-blast') light = .35 * charge * (released ? (1 - q) ** 2 : 1);
    for (let i = 0; i < WISPS; i++) {
      const k = KNOT + i, a = hash(i, this.seed), b = hash(i + 31, this.seed), c = hash(i + 67, this.seed);
      if (this.kind === 'shadow-blast') {
        // Once the knot is hurled the wisps still on their way in thin out where they are.
        const fadeIn = released ? (1 - q) ** 2 : 1;
        // Drawn up off the ground and in from round the caster into the palm, quicker as the charge builds.
        const age = (time * (.5 + .9 * charge) + a) % 1, from = new THREE.Vector3(Math.cos(b * 6.28), 0, Math.sin(b * 6.28)).multiplyScalar(.5 + .3 * c);
        point.copy(hand).addScaledVector(from, 1 - age).addScaledVector(UP, -(1 - age) * (hand.y - this.groundHeight(hand.x, hand.z)) * .8);
        smoke.set(k, point.x, point.y, point.z, .09 + .1 * (1 - age), .7 * Math.sin(Math.PI * age) * charge * fadeIn, .7, age + a, a);
      } else if (this.kind === 'summon-shadow') {
        // Dripping from the palm to the ground beneath it, heavier as the hand goes down; it stops as the pool takes it.
        const age = (time * .6 + a) % 1, ground = this.groundHeight(hand.x, hand.z), drip = (released ? 1 - q : charge) * Math.min(1, (1 - age) * 4);
        point.copy(hand).addScaledVector(side, (b - .5) * .1).addScaledVector(forward, (c - .5) * .1);
        point.y = THREE.MathUtils.lerp(hand.y, ground + .12, age * age);
        smoke.set(k, point.x, point.y, point.z, .07 + .09 * age, .75 * Math.sin(Math.PI * age) * drip, .5, age + a, a);
      } else if (!released) {
        // Command Shadows: wound round the claws, tightening as the sweep is drawn back.
        const age = (time * 1.4 + a) % 1, radius = (.06 + .16 * (1 - age)) * (1 - .4 * charge), angle = i * 2.399 + time * (3 + 6 * charge);
        point.copy(hand).addScaledVector(side, Math.cos(angle) * radius).addScaledVector(forward, Math.sin(angle) * radius).addScaledVector(UP, (b - .5) * .12);
        smoke.set(k, point.x, point.y, point.z, .08 + .06 * age, .7 * Math.sin(Math.PI * age) * charge, .7, age, a);
      } else {
        // Flung at the foe with the sweep in a narrow, ragged spray that thins as it goes.
        const reach = Math.pow(q, .6) * (.6 + a * 1.1);
        point.copy(hand).addScaledVector(forward, reach).addScaledVector(side, (b - .5) * reach * .35).addScaledVector(UP, (c - .5) * reach * .2);
        smoke.set(k, point.x, point.y, point.z, .1 + .2 * q, .8 * (1 - q), .6, q + a, a);
      }
    }
    for (let i = 0; i < POOL; i++) {
      const k = KNOT + WISPS + i;
      if (this.kind !== 'summon-shadow') { smoke.hide(k); continue; }
      // The pool: low puffs on the ground under the hand that spread as the charge builds; then it runs across the ground to the Shadow's spot,
      // arriving as the cast ends, and seeps away as the Shadow rises there.
      const a = hash(i + 101, this.seed), spot = cast.at, running = spot && i >= UNDER;
      let x: number, z: number, shown = smooth(p, .25, .5);
      if (running) {
        const t = (i - UNDER + 1) / (POOL - UNDER), run = smooth(p, .42, RELEASE), wobble = (a - .5) * .12;
        x = THREE.MathUtils.lerp(hand.x, spot.x, t) + wobble; z = THREE.MathUtils.lerp(hand.z, spot.z, t) - wobble;
        shown = smooth(run, t - .15, t);
      } else {
        const angle = i / UNDER * Math.PI * 2 + a, spread = smooth(p, .3, .78) * SHADE.pool * (.4 + .6 * hash(i + 7, this.seed));
        x = hand.x + Math.cos(angle) * spread; z = hand.z + Math.sin(angle) * spread;
      }
      // Each lifted by a little over a third of its size: a camera-facing card any lower cuts into the ground and flickers along the cut.
      const size = .25 + .3 * smooth(p, .3, .78);
      smoke.set(k, x, this.groundHeight(x, z) + size * .4, z, size * (1 + q * .4), .85 * shown * (1 - q) ** 1.5, .35, a, a);
      light = Math.max(light, .25 * smooth(p, .3, .7) * (1 - q));
    }
    smoke.commit(time);
    if (this.kind === 'command-shadows') light = released ? .3 * (1 - q) : .2 * charge;
    const at = this.kind === 'summon-shadow' ? new THREE.Vector3(hand.x, this.groundHeight(hand.x, hand.z) + .1, hand.z) : hand;
    this.halo.position.copy(at); this.halo.scale.setScalar(this.kind === 'summon-shadow' ? 1.6 : .7);
    // The palm's light throbs faintly; the pool's lies still.
    this.halo.material.opacity = light * (this.kind === 'summon-shadow' ? 1 : .85 + .15 * Math.sin(time * 9 + this.seed));
    return p < RELEASE + after;
  }
  dispose() { this.smoke.dispose(); this.halo.removeFromParent(); this.halo.material.dispose(); }
}
